/**
 * Report charts author their options for a desktop-width canvas: a value label on every point,
 * an axis title, and 14px of tick padding. Chart.js sizes the canvas to its container, so
 * nothing overflows — but on a phone that container is ~295px wide and the plot area inside it
 * only ~212px. Measured on /reports/contacts at a 375px viewport: 32 value labels sharing 212px
 * (~6px each, so they render as one smear of digits), and a 77px left gutter — 26% of the chart
 * — spent on an axis title that just repeats the section heading.
 *
 * So this is a legibility problem, not a layout one. Every value below is derived from the
 * chart's own measured width on each update, which means it tracks rotation and window resizes,
 * and never depends on the order the widths were seen in.
 *
 * Width rather than User-Agent, deliberately: a narrow desktop window is exactly as cramped as a
 * phone, and a tablet in landscape is not cramped at all.
 *
 * `beforeUpdate` (not `beforeLayout`) because it runs before Chart.js builds the scales for the
 * pass, so plain writes to `chart.options.scales` are picked up without having to reach into the
 * resolved `chart.scales[id].options` proxies. chartjs-plugin-datalabels resolves its own
 * options later still, during `afterDatasetUpdate`, so it sees these too.
 */

// horizontal room one value label needs before it collides with its neighbour
const LABEL_FOOTPRINT_PX = 34;

// under this width an axis title costs more room than the label explains
const AXIS_TITLE_MIN_WIDTH_PX = 480;

const NARROW_TICK_PADDING_PX = 4;

const NARROW_TICK_FONT_SIZE_PX = 10;

/**
 * The widget's own values, captured before this plugin has overridden anything.
 * Narrow-width settings are always recomputed from these rather than from whatever
 * was applied last, so widening a chart restores exactly what the widget asked for.
 */
const authoredOptions = chart => {
    if (chart.$nxAuthoredOptions) {
        return chart.$nxAuthoredOptions;
    }

    const scales = {};

    for (const [id, scale] of Object.entries(chart.options?.scales ?? {})) {
        scales[id] = {
            titleDisplay: scale?.title?.display,
            ticksPadding: scale?.ticks?.padding,
            ticksFontSize: scale?.ticks?.font?.size,
        };
    }

    chart.$nxAuthoredOptions = {
        dataLabelsDisplay: chart.options?.plugins?.datalabels?.display,
        scales,
    };

    return chart.$nxAuthoredOptions;
};

/**
 * How many value labels the chart would actually draw — one per point of every
 * visible dataset, which is what chartjs-plugin-datalabels renders. Counting the
 * x labels instead would under-count a multi-dataset chart by a factor of the
 * dataset count: a grouped bar chart divides each category horizontally, and a
 * multi-line chart stacks its labels on top of each other at the same x. Both
 * are just as unreadable as the single-series case this started from.
 */
const renderedLabelCount = chart => {
    const datasets = chart.data?.datasets ?? [];

    return datasets.reduce((total, dataset, index) => {
        // getDatasetMeta() builds the meta on demand, so this is safe on the
        // first pass, before the chart has drawn anything
        if (chart.isDatasetVisible && !chart.isDatasetVisible(index)) {
            return total;
        }

        return total + (dataset?.data?.length ?? 0);
    }, 0);
};

const applyTickFontSize = (ticks, size) => {
    if (size === undefined) {
        // the widget never set one — hand the size back to Chart.js' own default
        // rather than freezing whatever the narrow width happened to use
        if (ticks.font) {
            delete ticks.font.size;
        }

        return;
    }

    ticks.font = { ...(ticks.font ?? {}), size };
};

export default {
    id: 'nexlabResponsiveDensity',

    beforeUpdate(chart) {
        const width = chart.width;

        // null until Filament's ResizeObserver has measured the container; that
        // measurement triggers another update, which is the pass that matters
        if (!width) {
            return;
        }

        const authored = authoredOptions(chart);
        const options = chart.options;
        const isNarrow = width < AXIS_TITLE_MIN_WIDTH_PX;

        // labels stay only while the points are far enough apart to read. this is
        // what clears the smear of digits along the top of a phone-width chart,
        // and it leaves a 4-point chart labelled at any width
        const labelCount = renderedLabelCount(chart);
        const hasRoomForLabels = labelCount > 0 && labelCount <= Math.floor(width / LABEL_FOOTPRINT_PX);

        options.plugins ??= {};
        options.plugins.datalabels ??= {};
        options.plugins.datalabels.display = hasRoomForLabels ? (authored.dataLabelsDisplay ?? true) : false;

        for (const [id, authoredScale] of Object.entries(authored.scales)) {
            const scale = options.scales?.[id];

            // radial and arc charts have no cartesian scale to thin out
            if (!scale) {
                continue;
            }

            if (scale.title) {
                scale.title.display = isNarrow ? false : authoredScale.titleDisplay;
            }

            scale.ticks ??= {};
            scale.ticks.padding = isNarrow ? NARROW_TICK_PADDING_PX : authoredScale.ticksPadding;

            applyTickFontSize(scale.ticks, isNarrow ? NARROW_TICK_FONT_SIZE_PX : authoredScale.ticksFontSize);
        }
    },
};
